import React from 'react';

import { FormattedMessage, IntlShape } from 'react-intl';

import { statFormatter } from '@nhl/formatter-utilities';
import { ReactTableUtils } from '@nhl/nhl-breakaway-ui';
import { CellContext } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { PlayerPositionLong, PlayerStatAbbrev, PlayerStatNames } from '~/core/enums';

import { Goalie, GoalieSeasonStat, Skater, SkaterSeasonStat } from '../../types';

import {
    createColumn,
    GAMES_PLAYED_COL,
    getTimeFromMinutesSecondsLabel,
    NAME_COL,
    SWEATER_NUM_COL,
    TOI_COL,
} from './skaterColumns';

type RosterPlayer = Goalie | GoalieSeasonStat | Skater | SkaterSeasonStat;

const SAVE_PCTG_COL = (intl: IntlShape) => ({
    ...createColumn(intl, PlayerStatAbbrev.savePctg, PlayerStatNames.savePctg),
    cell: (cell: CellContext<RosterPlayer, number | string>) =>
        cell?.getValue() !== undefined ? (
            statFormatter.displayValueAs('savePercentage')(cell?.getValue())
        ) : (
            <FormattedMessage {...CommonMessages.statsDescriptions.noStatIndicator} />
        ),
});

const GA_OPTIONS = { size: 60 };
const GOALS_AGAINST_DIFFERENT_STRENGTHS = (intl: IntlShape) => [
    createColumn(intl, PlayerStatAbbrev.evenStrengthGoalsAgainst, PlayerStatNames.evenStrengthGoalsAgainst, GA_OPTIONS),
    createColumn(intl, PlayerStatAbbrev.powerPlayGoalsAgainst, PlayerStatNames.powerPlayGoalsAgainst, GA_OPTIONS),
    createColumn(intl, PlayerStatAbbrev.shortHandedGoalsAgainst, PlayerStatNames.shortHandedGoalsAgainst, GA_OPTIONS),
];

const GOALIE_PREGAME_COLS = (pos: PlayerPositionLong, isWebview: boolean, intl: IntlShape) => [
    SWEATER_NUM_COL(intl),
    NAME_COL(pos, isWebview, intl),
    GAMES_PLAYED_COL(intl),
    createColumn(intl, PlayerStatAbbrev.wins, PlayerStatNames.wins),
    createColumn(intl, PlayerStatAbbrev.losses, PlayerStatNames.losses),
    createColumn(intl, PlayerStatAbbrev.overtimeLosses, PlayerStatNames.overtimeLosses, { acc: 'otLosses' }),
    createColumn(intl, PlayerStatAbbrev.shotsAgainst, PlayerStatNames.shotsAgainst),
    createColumn(intl, PlayerStatAbbrev.saves, PlayerStatNames.saves),
    createColumn(intl, PlayerStatAbbrev.goalsAgainst, PlayerStatNames.goalsAgainst),
    SAVE_PCTG_COL(intl),
    {
        ...createColumn(intl, PlayerStatAbbrev.goalsAgainstAvg, PlayerStatNames.goalsAgainstAvg, {
            acc: 'goalsAgainstAvg',
        }),
        cell: ({ cell, row }) =>
            statFormatter.displayValueAs('goalsAgainstAverage')(cell?.getValue(), row.original.gamesPlayed >= 1),
    },
    {
        ...createColumn(intl, PlayerStatAbbrev.timeOnIce, PlayerStatNames.timeOnIce, { acc: 'toi' }),
        sortDescFirst: true,
        sortingFn: ReactTableUtils.sortUndefinedAtBottom(getTimeFromMinutesSecondsLabel),
    },
];

const GOALIE_LIVE_POST_GAME_COLS = (
    pos: PlayerPositionLong,
    isLimitedScoring: boolean,
    isWebview: boolean,
    intl: IntlShape
) => [
    SWEATER_NUM_COL(intl),
    NAME_COL(pos, isWebview, intl),
    createColumn(intl, PlayerStatAbbrev.shotsAgainst, PlayerStatNames.shotsAgainst),
    createColumn(intl, PlayerStatAbbrev.saves, PlayerStatNames.saves),
    createColumn(intl, PlayerStatAbbrev.goalsAgainst, PlayerStatNames.goalsAgainst),
    ...(isLimitedScoring ? [] : GOALS_AGAINST_DIFFERENT_STRENGTHS(intl)),
    SAVE_PCTG_COL(intl),
    TOI_COL(intl),
];

export const goalieColumns = (
    pos: PlayerPositionLong,
    isPregame: boolean,
    isLimitedScoring: boolean,
    isWebview: boolean,
    intl: IntlShape
) =>
    isPregame
        ? GOALIE_PREGAME_COLS(pos, isWebview, intl)
        : GOALIE_LIVE_POST_GAME_COLS(pos, isLimitedScoring, isWebview, intl);
